import React, { useState } from 'react';
import { leadsAPI } from '../../services/api';
import RequiredFieldLabel from '../Common/RequiredFieldLabel';
import { validateStatusHistory, hasValidationErrors, getErrorMessage } from '../../utils/validation';

function LeadHistoryModal({ lead, onClose, users, onUpdateHistory }) {
  const [activeTab, setActiveTab] = useState('status');
  const [editingHistoryId, setEditingHistoryId] = useState(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [newRating, setNewRating] = useState({ rating: '', reason: '' });
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const getUsername = (changedBy) => {
    if (!changedBy) return 'System';
    if (typeof changedBy === 'object' && changedBy.username) return changedBy.username;
    const user = users.find((u) => u._id === changedBy || u._id === changedBy?._id);
    return user ? user.username : 'Unknown User';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Closed Won': return 'bg-green-100 text-green-800 border-green-200';
      case 'Closed Lost': return 'bg-red-100 text-red-800 border-red-200';
      case 'Cold Lead': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'Prospecting': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Qualified': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Demo Scheduled / Done': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Evaluation / Pilot': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'Decision': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getRatingForStatus = (status) => {
    if (!lead.ratingHistory) return null;
    const ratings = lead.ratingHistory.filter(r => r.progressStatus === status);
    return ratings.length > 0 ? ratings[ratings.length - 1] : null;
  };

  const addRating = async () => {
    if (!newRating.rating || !selectedStatus) return;
    
    setLoading(true);
    try {
      await leadsAPI.addRating(lead._id, {
        rating: parseFloat(newRating.rating),
        reason: newRating.reason,
        progressStatus: selectedStatus
      });
      setShowRatingModal(false);
      setNewRating({ rating: '', reason: '' });
      setSelectedStatus('');
      // Refresh the parent component to show updated data
      if (onUpdateHistory) {
        onUpdateHistory();
      }
      onClose();
    } catch (error) {
      alert('Failed to add rating: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (history) => {
    setEditingHistoryId(history._id);
    setEditedDescription(history.description || '');
  };

  const saveDescription = (leadId, historyId) => {
    const errors = validateStatusHistory({ description: editedDescription });
    setValidationErrors(errors);
    
    if (hasValidationErrors(errors)) {
      return;
    }
    
    onUpdateHistory(leadId, historyId, editedDescription);
    setEditingHistoryId(null);
    setEditedDescription('');
    setValidationErrors({});
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white">Lead History</h2>
              <p className="text-blue-100 mt-1">{lead.schoolName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('status')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'status'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Status History
            </button>
            <button
              onClick={() => setActiveTab('assignment')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'assignment'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Assignment History
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {activeTab === 'status' ? (
            !lead.statusHistory || lead.statusHistory.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No History Available</h3>
              <p className="text-gray-500">This lead doesn't have any status history yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {lead.statusHistory
                .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                .map((history, index) => (
                <div key={history._id || index} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    {/* Status and User Info */}
                    <div className="flex-shrink-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(history.newStatus)}`}>
                          {history.oldStatus ? `${history.oldStatus} → ${history.newStatus}` : history.newStatus || 'Status Update'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="font-medium">{getUsername(history.changedBy)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{formatDate(history.updatedAt)}</span>
                      </div>
                    </div>

                    {/* Description and Rating */}
                    <div className="flex-1">
                      {editingHistoryId === history._id ? (
                        <div className="space-y-3">
                          <div>
                            <RequiredFieldLabel required>Description</RequiredFieldLabel>
                            <textarea
                              value={editedDescription}
                              onChange={(e) => setEditedDescription(e.target.value)}
                              className={`w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${getErrorMessage(validationErrors, 'description') ? 'border-red-500' : ''}`}
                              rows="3"
                              placeholder="Enter description..."
                            />
                            {getErrorMessage(validationErrors, 'description') && (
                              <p className="text-red-500 text-sm mt-1">{getErrorMessage(validationErrors, 'description')}</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveDescription(lead._id, history._id)}
                              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm font-medium"
                            >
                              Save Changes
                            </button>
                            <button
                              onClick={() => {
                                setEditingHistoryId(null);
                                setEditedDescription('');
                              }}
                              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-sm font-medium"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="group space-y-3">
                          <p className="text-gray-700 leading-relaxed">
                            {history.description || 'No description provided'}
                          </p>
                          
                          {/* Rating Section */}
                          {history.newStatus && (
                            <div className="bg-gray-50 p-3 rounded-lg">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-sm font-medium text-gray-700">Rating for {history.newStatus}:</span>
                                  {getRatingForStatus(history.newStatus) ? (
                                    <div className="mt-1">
                                      <span className="text-lg font-bold text-blue-600">
                                        {getRatingForStatus(history.newStatus).rating}/10
                                      </span>
                                      <p className="text-xs text-gray-600 mt-1">
                                        {getRatingForStatus(history.newStatus).reason}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        By {getRatingForStatus(history.newStatus).givenBy?.username || 'Admin'} on {formatDate(getRatingForStatus(history.newStatus).createdAt)}
                                      </p>
                                    </div>
                                  ) : (
                                    <span className="text-sm text-gray-500 ml-2">No rating given</span>
                                  )}
                                </div>
                                <button
                                  onClick={() => {
                                    setSelectedStatus(history.newStatus);
                                    setShowRatingModal(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-700 text-sm font-medium px-3 py-1 rounded border border-blue-200 hover:bg-blue-50 transition-colors"
                                >
                                  {getRatingForStatus(history.newStatus) ? 'Update Rating' : 'Add Rating'}
                                </button>
                              </div>
                            </div>
                          )}
                          
                          <button
                            onClick={() => startEditing(history)}
                            className="opacity-0 group-hover:opacity-100 text-blue-600 hover:text-blue-700 text-sm font-medium transition-all duration-200 flex items-center gap-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit Description
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
           )
          
          ) : (
            // Assignment History Tab
            !lead.assignmentHistory || lead.assignmentHistory.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.196-2.121M9 6a3 3 0 106 0 3 3 0 00-6 0zM15 20a3 3 0 01-6 0v-1a3 3 0 013-3h0a3 3 0 013 3v1z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Assignment History</h3>
                <p className="text-gray-500">This lead doesn't have any assignment changes yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {lead.assignmentHistory
                  .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                  .map((assignment, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          assignment.type === 'primary' ? 'bg-blue-100' : 'bg-green-100'
                        }`}>
                          <svg className={`w-5 h-5 ${
                            assignment.type === 'primary' ? 'text-blue-600' : 'text-green-600'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            assignment.type === 'primary' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {assignment.type === 'primary' ? 'Primary Assignee' : 'Secondary Assignees'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-700 mb-2">
                          {assignment.type === 'primary' ? (
                            <span>
                              <span className="font-medium">Changed from:</span> {assignment.oldAssignee ? getUsername(assignment.oldAssignee) : 'None'} 
                              <span className="mx-2">→</span> 
                              <span className="font-medium">to:</span> {assignment.newAssignee ? getUsername(assignment.newAssignee) : 'None'}
                            </span>
                          ) : (
                            <div>
                              <div className="mb-1">
                                <span className="font-medium">Previous:</span> {assignment.oldAssignees?.length ? assignment.oldAssignees.map(id => getUsername(id)).join(', ') : 'None'}
                              </div>
                              <div>
                                <span className="font-medium">Current:</span> {assignment.newAssignees?.length ? assignment.newAssignees.map(id => getUsername(id)).join(', ') : 'None'}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span>Changed by {getUsername(assignment.changedBy)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{formatDate(assignment.changedAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Rating Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-60 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">Add Rating for {selectedStatus}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rating (0-10)</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={newRating.rating}
                  onChange={(e) => setNewRating({ ...newRating, rating: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter rating"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                <textarea
                  value={newRating.reason}
                  onChange={(e) => setNewRating({ ...newRating, reason: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Reason for this rating"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={addRating}
                  disabled={loading || !newRating.rating}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Adding...' : 'Add Rating'}
                </button>
                <button
                  onClick={() => {
                    setShowRatingModal(false);
                    setNewRating({ rating: '', reason: '' });
                    setSelectedStatus('');
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadHistoryModal;